import { Fragment, useEffect, useMemo, useState } from "react";
import { useApi } from "@/hooks/useApi";
import { apiGet } from "@/lib/api";

// 视频图片管理 —— 把点位段落上传的图片(media_type='image')与视频(media_type='video')
// 一并按景点聚合展示。
// 三个 tab：[全部] 图片+视频混合；[视频] 仅视频；[图片] 仅图片。
// 上传仍在「点位内容 → 段落」里做，这里只做统一浏览/预览。

type Tab = "all" | "video" | "image";

interface SceneRow {
  id: string;
  sceneName: string;
  imageCount: number;
  videoCount: number;
  sizeBytes: number;
  durationSec: number;
  updatedAt: string;
}
interface SegmentMedia {
  part: string;
  variant: string;
  mediaType: "image" | "video";
  mediaUrl: string;
  sizeBytes: number;
  durationSec: number;
  updatedAt: string;
}
interface MediaStats {
  totalScenes: number;
  scenesWithMedia: number;
  scenesWithVideo: number;
  scenesWithImage: number;
  totalImages: number;
  totalVideos: number;
  totalSizeBytes: number;
  totalDurationSec: number;
}

const fmtSize = (b: number) =>
  b >= 1 << 30 ? `${(b / (1 << 30)).toFixed(2)} GB`
    : b >= 1 << 20 ? `${(b / (1 << 20)).toFixed(1)} MB`
      : b > 0 ? `${Math.max(1, Math.round(b / 1024))} KB` : "—";
const fmtDur = (s: number) =>
  s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : s > 0 ? `${s}s` : "—";

const PART_LABEL: Record<string, string> = {
  segment_1: "段落一",
  segment_2: "段落二",
  question: "提问",
  answer_yes: "回答·是",
  answer_no: "回答·否",
};
const VARIANT_LABEL: Record<string, string> = {
  concise: "公务版",
  story: "故事版",
  talky: "话痨版",
};
const prettyPart = (p: string) => {
  if (PART_LABEL[p]) return PART_LABEL[p];
  const m = p.match(/^content_(\d+)$/);
  if (m) return `第 ${m[1]} 段`;
  const q = p.match(/^prompt_(\d+)$/);
  if (q) return `问答 ${q[1]}`;
  const s = p.match(/^segment_(\d+)$/);
  if (s) return `段落 ${s[1]}`;
  return p;
};

// Tab → 列表/明细接口的映射。把 endpoint 集中在一处方便走同一份渲染。
const TAB_CFG: Record<Tab, { list: string; stats: string; segment: (id: string) => string }> = {
  all:   { list: "/scene-media",  stats: "/scene-media/stats",  segment: (id) => `/scenes/${id}/segment-media` },
  video: { list: "/scene-videos", stats: "/scene-videos/stats", segment: (id) => `/scenes/${id}/segment-videos` },
  image: { list: "/scene-images", stats: "/scene-images/stats", segment: (id) => `/scenes/${id}/segment-images` },
};

function VideosPage() {
  const [tab, setTab] = useState<Tab>("all");
  const cfg = TAB_CFG[tab];

  // 切 tab 后重新请求；后端没数据字段对齐时前端做兜底。
  const { data: rawItems, reload } = useApi<SceneRow[]>(cfg.list, []);
  const { data: stats } = useApi<MediaStats>(cfg.stats, {
    totalScenes: 0, scenesWithMedia: 0, scenesWithVideo: 0, scenesWithImage: 0,
    totalImages: 0, totalVideos: 0, totalSizeBytes: 0, totalDurationSec: 0,
  });
  // /scene-videos & /scene-images 各自只返回自己的 count，补齐到 SceneRow 形态。
  const items: SceneRow[] = useMemo(() => rawItems.map((it) => ({
    id: it.id, sceneName: it.sceneName,
    imageCount: it.imageCount ?? 0,
    videoCount: it.videoCount ?? 0,
    sizeBytes: it.sizeBytes ?? 0,
    durationSec: it.durationSec ?? 0,
    updatedAt: it.updatedAt ?? "",
  })), [rawItems]);

  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [cache, setCache] = useState<Record<string, SegmentMedia[]>>({});
  const [preview, setPreview] = useState<{ type: "video" | "image"; url: string; title: string } | null>(null);

  useEffect(() => { setExpandedId(null); setCache({}); }, [tab]);

  // 老 /scene-videos 和 /scene-images 返回的 segment 列表不含 mediaType；
  // 按当前 tab 推断（video tab → 'video'，image tab → 'image'）。
  const toggle = async (item: SceneRow) => {
    if (expandedId === item.id) { setExpandedId(null); return; }
    setExpandedId(item.id);
    if (!cache[item.id]) {
      try {
        const raw = await apiGet<Partial<SegmentMedia>[]>(cfg.segment(item.id));
        const filled: SegmentMedia[] = (raw || []).map((m) => ({
          part: m.part ?? "",
          variant: m.variant ?? "",
          mediaType: (m.mediaType as "image" | "video") ?? (tab === "image" ? "image" : "video"),
          mediaUrl: m.mediaUrl ?? "",
          sizeBytes: m.sizeBytes ?? 0,
          durationSec: m.durationSec ?? 0,
          updatedAt: m.updatedAt ?? "",
        }));
        setCache((prev) => ({ ...prev, [item.id]: filled }));
      } catch { /* panel shows 暂无 */ }
    }
  };

  // Esc closes the preview.
  useEffect(() => {
    if (!preview) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPreview(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview]);

  const filtered = useMemo(
    () => items.filter((i) => search === "" || i.sceneName.includes(search)),
    [items, search],
  );

  const TABS: { key: Tab; label: string; icon: string }[] = [
    { key: "all",   label: "全部", icon: "ri-stack-line" },
    { key: "video", label: "视频", icon: "ri-film-line" },
    { key: "image", label: "图片", icon: "ri-image-line" },
  ];

  // 统计卡片随 tab 切换变化（突出当前 tab 的核心数）。
  const statCards = tab === "video"
    ? [
        { label: "总景点数", value: `${stats.totalScenes}`, cls: "bg-white border-gray-200 text-gray-900" },
        { label: "有视频的景点", value: `${stats.scenesWithVideo}`, cls: "bg-teal-50 border-teal-200 text-teal-700" },
        { label: "视频段总数", value: `${stats.totalVideos}`, cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
        { label: "占用存储", value: fmtSize(stats.totalSizeBytes), cls: "bg-indigo-50 border-indigo-200 text-indigo-700" },
        { label: "总时长", value: fmtDur(stats.totalDurationSec), cls: "bg-amber-50 border-amber-200 text-amber-700" },
      ]
    : tab === "image"
    ? [
        { label: "总景点数", value: `${stats.totalScenes}`, cls: "bg-white border-gray-200 text-gray-900" },
        { label: "有图片的景点", value: `${stats.scenesWithImage}`, cls: "bg-teal-50 border-teal-200 text-teal-700" },
        { label: "图片段总数", value: `${stats.totalImages}`, cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
        { label: "占用存储", value: fmtSize(stats.totalSizeBytes), cls: "bg-indigo-50 border-indigo-200 text-indigo-700" },
      ]
    : [
        { label: "总景点数", value: `${stats.totalScenes}`, cls: "bg-white border-gray-200 text-gray-900" },
        { label: "有素材的景点", value: `${stats.scenesWithMedia}`, cls: "bg-teal-50 border-teal-200 text-teal-700" },
        { label: "图片段总数", value: `${stats.totalImages}`, cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
        { label: "视频段总数", value: `${stats.totalVideos}`, cls: "bg-purple-50 border-purple-200 text-purple-700" },
        { label: "占用存储", value: fmtSize(stats.totalSizeBytes), cls: "bg-indigo-50 border-indigo-200 text-indigo-700" },
        { label: "视频总时长", value: fmtDur(stats.totalDurationSec), cls: "bg-amber-50 border-amber-200 text-amber-700" },
      ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">视频图片管理</h1>
          <p className="text-sm text-gray-500 mt-1">点位讲解段落图片与视频 · 按景点聚合预览（上传在「点位内容 → 段落」里进行）</p>
        </div>
        <button onClick={reload} className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm whitespace-nowrap">
          <i className="ri-refresh-line mr-1.5" />刷新
        </button>
      </div>

      {/* tab 切换 */}
      <div className="flex items-center gap-2 flex-wrap border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-teal-600 text-teal-700" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className={`${t.icon} mr-1.5`} />{t.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {statCards.map((c) => (
          <div key={c.label} className={`border rounded-lg p-4 ${c.cls.split(" ").slice(0, 2).join(" ")}`}>
            <div className="text-xs text-gray-500 mb-1">{c.label}</div>
            <div className={`text-2xl font-bold ${c.cls.split(" ")[2]}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* List */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            共 {filtered.length} 个{tab === "image" ? "含图片" : tab === "video" ? "含视频" : "含素材"}的景点
          </span>
          <div className="relative w-64">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="搜索景点名称" className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center text-center">
            <i className={`text-4xl text-gray-300 mb-3 ${tab === "image" ? "ri-image-line" : tab === "video" ? "ri-film-line" : "ri-stack-line"}`} />
            <p className="text-sm text-gray-400">暂无{tab === "image" ? "图片" : tab === "video" ? "视频" : "素材"}。请到「点位内容 → 选择景点 → 段落」上传。</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">景点</th>
                  {tab !== "image" && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">视频段数</th>
                  )}
                  {tab !== "video" && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">图片段数</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">大小</th>
                  {tab !== "image" && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">视频时长</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">更新时间</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.map((item) => {
                  const colSpan = 3 + (tab !== "image" ? 2 : 0) + (tab !== "video" ? 1 : 0);
                  return (
                    <Fragment key={item.id}>
                      <tr onClick={() => toggle(item)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-gray-900">
                            <i className={`mr-1 text-gray-400 ${expandedId === item.id ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"}`} />
                            {item.sceneName}
                          </span>
                        </td>
                        {tab !== "image" && (
                          <td className="px-4 py-3">
                            {item.videoCount > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                <i className="ri-film-line" />{item.videoCount}
                              </span>
                            ) : <span className="text-xs text-gray-300">—</span>}
                          </td>
                        )}
                        {tab !== "video" && (
                          <td className="px-4 py-3">
                            {item.imageCount > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                <i className="ri-image-line" />{item.imageCount}
                              </span>
                            ) : <span className="text-xs text-gray-300">—</span>}
                          </td>
                        )}
                        <td className="px-4 py-3"><span className="text-sm text-gray-700">{fmtSize(item.sizeBytes)}</span></td>
                        {tab !== "image" && (
                          <td className="px-4 py-3"><span className="text-sm text-gray-700">{fmtDur(item.durationSec)}</span></td>
                        )}
                        <td className="px-4 py-3"><span className="text-sm text-gray-500">{item.updatedAt}</span></td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <a href={`/knowledge/scenes/${item.id}`} className="w-7 h-7 inline-flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" title="景点详情 / 上传">
                            <i className="ri-external-link-line" />
                          </a>
                        </td>
                      </tr>
                      {expandedId === item.id && (
                        <tr className="bg-teal-50/40">
                          <td colSpan={colSpan} className="px-6 py-4">
                            <MediaGrid
                              tab={tab}
                              items={cache[item.id]}
                              onOpen={(m) => setPreview({
                                type: m.mediaType,
                                url: m.mediaUrl,
                                title: `${item.sceneName} · ${VARIANT_LABEL[m.variant] ?? m.variant} · ${prettyPart(m.part)}`,
                              })}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview modal —— video / image 共用同一蒙层 */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setPreview(null)}>
          <div className="bg-black rounded-lg overflow-hidden max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-2.5 flex items-center justify-between bg-gray-900">
              <span className="text-sm text-gray-200 truncate">{preview.title}</span>
              <button onClick={() => setPreview(null)} className="text-gray-400 hover:text-white"><i className="ri-close-line text-xl" /></button>
            </div>
            {preview.type === "video" ? (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video src={preview.url} controls autoPlay className="w-full max-h-[75vh] bg-black" />
            ) : (
              <img src={preview.url} alt={preview.title} className="w-full max-h-[75vh] object-contain bg-black" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// 一个景点下所有图片+视频段：按 风格(variant) 分组，每段一个卡片。
// 在「全部」tab 下，每个卡片左上角带 image/video 角标区分类型。
function MediaGrid({ tab, items, onOpen }: { tab: Tab; items?: SegmentMedia[]; onOpen: (m: SegmentMedia) => void }) {
  if (!items) return <div className="text-sm text-gray-400 py-2"><i className="ri-loader-4-line animate-spin mr-1" />加载素材…</div>;
  if (!items.length) return <div className="text-sm text-gray-400 py-2">该景点暂无{tab === "image" ? "图片" : tab === "video" ? "视频" : "素材"}段</div>;
  const variants = [...new Set(items.map((v) => v.variant))];
  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-500"><i className="ri-eye-line mr-1" />点击卡片预览对应 风格 · 段落 的素材</div>
      {variants.map((vr) => (
        <div key={vr} className="flex items-start gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-700 w-16 shrink-0 pt-1.5">{VARIANT_LABEL[vr] ?? vr}</span>
          <div className="flex gap-2 flex-wrap">
            {items.filter((v) => v.variant === vr).map((m) => {
              const isImg = m.mediaType === "image";
              return (
                <button
                  key={`${m.mediaType}-${m.variant}-${m.part}`}
                  onClick={(e) => { e.stopPropagation(); onOpen(m); }}
                  className={`relative rounded-lg border transition-colors text-left overflow-hidden ${
                    isImg
                      ? "bg-white border-purple-200 hover:bg-purple-50"
                      : "bg-white border-teal-200 hover:bg-teal-50"
                  }`}
                  title={isImg ? "查看大图" : "预览视频"}
                  style={{ width: 168 }}
                >
                  <div className="relative w-full h-24 bg-gray-100 flex items-center justify-center text-gray-400">
                    {isImg ? (
                      <img src={m.mediaUrl} alt={m.part} className="w-full h-full object-cover" />
                    ) : (
                      <i className="ri-play-circle-fill text-4xl text-teal-500" />
                    )}
                    <span className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${isImg ? "bg-purple-600 text-white" : "bg-teal-600 text-white"}`}>
                      <i className={`${isImg ? "ri-image-line" : "ri-film-line"} mr-0.5`} />
                      {isImg ? "图片" : "视频"}
                    </span>
                  </div>
                  <div className="px-2 py-1.5">
                    <div className={`text-xs font-medium ${isImg ? "text-purple-700" : "text-teal-700"}`}>{prettyPart(m.part)}</div>
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      {fmtSize(m.sizeBytes)}{!isImg && m.durationSec ? ` · ${fmtDur(m.durationSec)}` : ""}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default VideosPage;
